import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ModalBody, ModalHeader } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';

import ModalLayout from '../ModalLayout';
import {
  ReminderInstanceCalendarType,
  ReminderInstanceSignalType,
} from '../ProgressBars';
import { MAINTENANCE_NOTIFICATION_STATUSES } from '../../Constants/GatewayReminderInstances';
import {
  ALL_TYPE,
  CALENDAR_TYPE,
  GTEMPLATE_REMINDER_TYPES,
  SIGNAL_TYPE,
} from '../../Constants/GtemplateReminder';
import {
  getDateTriggered,
  getServiceDetails,
} from '../../Lib/GatewayReminderInstance';

export default memo(GatewayInstanceMobileMaintenanceCardDetails);

function GatewayInstanceMobileMaintenanceCardDetails(props) {
  const { instance, toggleHistoryModal } = props,
    showHistoryClickHandler = useCallback(() => {
      toggleHistoryModal?.({ gateway_reminder_instance_id: instance?._id });
    }, [instance?._id, toggleHistoryModal]),
    dateTriggered = getDateTriggered(instance),
    { contact_info, last_service_date } = getServiceDetails(instance),
    { display_precision, unit } =
      props.signalUnitMap.get(instance.GtemplateReminder?.signal_id) || {},
    {
      calendar_end,
      calendar_percentage,
      calendar_progress,
      calendar_text,
      calendar_upcoming,
      signal_current,
      signal_end,
      signal_margin,
      signal_percentage,
      signal_progress,
      signal_text,
    } = props.instanceShadowValueMap.get(instance._id),
    isCompletedOneTimeReminder = useMemo(
      () =>
        !instance.GtemplateReminder?.recurring &&
        !!instance.MaintenanceHistoryLogs?.length,
      [
        instance.GtemplateReminder?.recurring,
        instance.MaintenanceHistoryLogs?.length,
      ]
    ),
    [{ signal_value_when_serviced } = {}] =
      instance.MaintenanceHistoryLogs ?? [];

  return (
    <ModalLayout centered isOpen={props.isOpen} scrollable>
      <ModalHeader
        className="border-bottom-0"
        toggle={() => props.toggleModal(!props.isOpen)}
      >
        <p className="font-weight-normal">Details</p>
      </ModalHeader>
      <ModalBody className="d-flex flex-column text-muted align-items-center">
        <div className="w-100 mb-4">
          <p className="text-third-gray font-weight-light three-quarter-text mb-2">
            Reminder Name
          </p>
          <p className="text-muted font-weight-bolder mb-0">
            {instance?.GtemplateReminder?.name ?? ''}
          </p>
        </div>
        <div className="w-100 d-flex flex-column mb-4">
          <p className="text-third-gray font-weight-light three-quarter-text mb-2">
            Status
          </p>
          {(() => {
            switch (instance.GtemplateReminder.type) {
              case ALL_TYPE:
                return (
                  <>
                    <div className="mt-2 mb-1">
                      <ReminderInstanceCalendarType
                        calendar_end={calendar_end}
                        calendar_percentage={calendar_percentage}
                        calendar_upcoming={calendar_upcoming}
                        instance={instance}
                        isCompletedOneTimeReminder={isCompletedOneTimeReminder}
                        progress_color={calendar_progress}
                        text={calendar_text}
                      />
                    </div>
                    <div className="mt-1">
                      <ReminderInstanceSignalType
                        display_precision={display_precision}
                        instance={instance}
                        isCompletedOneTimeReminder={isCompletedOneTimeReminder}
                        progress_color={signal_progress}
                        signal_current={signal_current}
                        signal_end={signal_end}
                        signal_margin={signal_margin}
                        signal_percentage={signal_percentage}
                        signal_value_when_serviced={signal_value_when_serviced}
                        text={signal_text}
                        unit={unit}
                      />
                    </div>
                  </>
                );
              case CALENDAR_TYPE:
                return (
                  <ReminderInstanceCalendarType
                    calendar_end={calendar_end}
                    calendar_percentage={calendar_percentage}
                    calendar_upcoming={calendar_upcoming}
                    instance={instance}
                    isCompletedOneTimeReminder={isCompletedOneTimeReminder}
                    progress_color={calendar_progress}
                    text={calendar_text}
                  />
                );
              case SIGNAL_TYPE:
                return (
                  <ReminderInstanceSignalType
                    instance={instance}
                    isCompletedOneTimeReminder={isCompletedOneTimeReminder}
                    progress_color={signal_progress}
                    signal_current={signal_current}
                    signal_end={signal_end}
                    signal_margin={signal_margin}
                    signal_percentage={signal_percentage}
                    signal_value_when_serviced={signal_value_when_serviced}
                    text={signal_text}
                    unit={unit}
                  />
                );
              default:
                return null;
            }
          })()}
        </div>
        <div className="w-100 mb-4">
          <p className="text-third-gray font-weight-light three-quarter-text mb-2">
            Date Triggered
          </p>
          <p className="text-muted font-weight-bolder mb-0">{dateTriggered}</p>
        </div>
        <div className="w-100 mb-4">
          <p className="text-third-gray font-weight-light three-quarter-text mb-2">
            Last Service Date
          </p>
          <p className="text-muted font-weight-bolder mb-0">
            {last_service_date}
          </p>
        </div>
        <div className="w-100 mb-4">
          <p className="text-third-gray font-weight-light three-quarter-text mb-2">
            Last Service Done By
          </p>
          <p className="text-muted font-weight-bolder mb-0">{contact_info}</p>
        </div>
        <div className="w-100 mb-4">
          <button
            className="btn btn-block btn-outline-secondary"
            onClick={showHistoryClickHandler}
          >
            View History
          </button>
        </div>
        <div className="w-100 mb-4">
          <a
            className="btn btn-block modal-button-primary"
            href={instance.GtemplateReminder?.attachment}
            rel="noopener noreferrer"
            target="_blank"
          >
            View Attachment
            <FontAwesomeIcon className="ml-3" icon={faFilePdf} size="lg" />
          </a>
        </div>
      </ModalBody>
    </ModalLayout>
  );
}

GatewayInstanceMobileMaintenanceCardDetails.propTypes = {
  instance: PropTypes.shape({
    _id: PropTypes.string,
    GtemplateReminder: PropTypes.shape({
      attachment: PropTypes.string,
      message: PropTypes.string,
      name: PropTypes.string,
      recurring: PropTypes.bool,
      signal_id: PropTypes.string,
      trigger_conditions: PropTypes.shape({
        calendar_trigger: PropTypes.shape({
          interval: PropTypes.string,
          margin: PropTypes.string,
        }),
        signal_trigger: PropTypes.shape({
          interval: PropTypes.number,
          margin: PropTypes.number,
        }),
      }),
      type: PropTypes.oneOf(GTEMPLATE_REMINDER_TYPES),
    }),
    MaintenanceHistoryLogs: PropTypes.arrayOf(PropTypes.object),
    MaintenanceNotifications: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        maintenance_completed: PropTypes.bool,
        signal_value: PropTypes.string,
        status: PropTypes.oneOf(MAINTENANCE_NOTIFICATION_STATUSES),
        MaintenanceHistoryLog: PropTypes.shape({
          contact_info: PropTypes.string,
          message: PropTypes.string,
          service_date: PropTypes.string,
          signal_value_when_serviced: PropTypes.string,
          user_id: PropTypes.string,
        }),
      })
    ),
    next_signal_value_due: PropTypes.string,
    next_time_due: PropTypes.string,
  }),
  instanceShadowValueMap: PropTypes.object,
  isOpen: PropTypes.bool,
  signalUnitMap: PropTypes.object,
  showHistoryModal: PropTypes.string,
  toggleHistoryModal: PropTypes.func,
  toggleModal: PropTypes.func,
};
